import axios from 'axios';
import { plainToClass, plainToClassFromExist, classToPlain, deserialize, Exclude, Expose, Type } from 'class-transformer';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0

THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.

See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */

function __awaiter(thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __generator(thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
}

var callbacksStart = [];
var callbacksEnd = [];
var callbacksError = [];
var RequestListener = {
    emitRequestStart: function (name) {
        this.emitRequest(name, callbacksStart);
    },
    emitRequestEnd: function (name) {
        this.emitRequest(name, callbacksEnd);
    },
    emitRequestError: function (name) {
        this.emitRequest(name, callbacksError);
    },
    emitRequest: function (name, callbacks) {
        if (callbacks) {
            callbacks.forEach(function (c) { return c(name); });
        }
    },
    onRequestStart: function (cb) {
        callbacksStart.push(cb);
    },
    onRequestEnd: function (cb) {
        callbacksEnd.push(cb);
    },
    onRequestError: function (cb) {
        callbacksError.push(cb);
    },
    removeStartListener: function (cb) {
        this.removeListenerOf(cb, callbacksStart);
    },
    removeEndListener: function (cb) {
        this.removeListenerOf(cb, callbacksEnd);
    },
    removeErrorListener: function (cb) {
        this.removeListenerOf(cb, callbacksError);
    },
    removeListenerOf: function (cb, callbacks) {
        var index = callbacks.indexOf(cb);
        if (index > -1) {
            callbacks.splice(index, 1);
        }
    },
    removeListener: function (cb) {
        this.removeStartListener(cb);
        this.removeEndListener(cb);
        this.removeErrorListener(cb);
    },
    clearStartListeners: function () {
        this.clearListenersOf(callbacksStart);
    },
    clearEndListeners: function () {
        this.clearListenersOf(callbacksEnd);
    },
    clearErrorListeners: function () {
        this.clearListenersOf(callbacksError);
    },
    clearListenersOf: function (callbacks) {
        callbacks.splice(0, callbacks.length);
    },
    clearListeners: function () {
        this.clearStartListeners();
        this.clearEndListeners();
        this.clearErrorListeners();
    },
    startListenerCount: function (cb) {
        if (cb === void 0) { cb = null; }
        return this.listenerCount(cb, callbacksStart);
    },
    endListenerCount: function (cb) {
        if (cb === void 0) { cb = null; }
        return this.listenerCount(cb, callbacksEnd);
    },
    errorListenerCount: function (cb) {
        if (cb === void 0) { cb = null; }
        return this.listenerCount(cb, callbacksError);
    },
    listenerCount: function (cb, callbacks) {
        if (cb === void 0) { cb = null; }
        if (!cb) {
            return callbacks.length;
        }
        return callbacks.indexOf(cb) === -1 ? 0 : 1;
    },
};

var RequestConfig = {
    axios: axios.create(),
};

/**
 * The Response is an auxiliary class of [[Request]].
 *
 * Although you may use this class to make HTTP requests, it is recommended to use [[Request]] instead.
 *
 * By using [[Request]], the code will be semantically better.
 *
 * It uses [Axios](https://github.com/axios/axios) to request; therefore, you may configure an Axios instance.
 *
 * ## Example of configuration
 * ```typescript
 * import Simpli from 'simpli-web-sdk'
 * import axios from 'axios'
 *
 * const axiosInstance = axios.create({
 *  baseURL: 'http://example.com/api'
 * })
 *
 * Simpli.axios = axiosInstance
 *
 * Simpli.install()
 * ```
 *
 * ## Example
 * ```typescript
 * import {Request, Response} from 'simpli-web-sdk'
 * import {User} from './User'
 *
 * async function example() {
 *   const request = new Request({url: '/path/to/url', method: 'GET'})
 *   const response = new Response(request, User)
 *   return await response.getData() // User type
 * }
 * ```
 */
var Response = /** @class */ (function () {
    /**
     * Assigns a new response instance with a provided request instance.
     *
     * ```typescript
     * import {Request, Response} from 'simpli-web-sdk'
     *
     * async function example() {
     *   const request = new Request({url: '/path/to/url', method: 'GET'})
     *   const response = new Response(request, User)
     * }
     * ```
     * @param request A request instance
     * @param responseType The response type. You may use either an instance object or a class definition.
     * If it is an instance, then the response will populate it.
     */
    function Response(request, responseType) {
        this.request = request;
        this.responseType = responseType;
    }
    Object.defineProperty(Response.prototype, "axiosConfig", {
        /**
         * Get the Axios configuration of the provided [[Request]].
         * More details in [Axios Docs](https://github.com/axios/axios#request-config)
         * @hidden
         */
        get: function () {
            return this.request.axiosConfig;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Response.prototype, "requestName", {
        /**
         * Get the name of the provided [[Request]].
         * @hidden
         */
        get: function () {
            return this.request.requestName;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Response.prototype, "requestDelay", {
        /**
         * Get the delay of the provided [[Request]].
         * @hidden
         */
        get: function () {
            return this.request.requestDelay;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(Response.prototype, "endpoint", {
        /**
         * Get the endpoint of the provided [[Request]].
         * @hidden
         */
        get: function () {
            return this.request.endpoint;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Gives a name of this request which is used in [[Await]] component.
     *
     * The same effect of [request.name()](./request.html#name)
     */
    Response.prototype.name = function (requestName) {
        this.request.requestName = requestName;
        return this;
    };
    /**
     * Provides a delay of any request from this instance.
     *
     * The same effect of [request.delay()](./request.html#delay)
     */
    Response.prototype.delay = function (requestDelay) {
        this.request.requestDelay = Math.max(requestDelay, 0);
        return this;
    };
    /**
     * Makes a HTTP request and returns the data content from response.
     *
     * ```typescript
     * import {Request} from 'simpli-web-sdk'
     *
     * async function example1() {
     *   return await Request.get('/path/to/url')
     *     .asAny()
     *     .getData()
     * }
     *
     * // the same of
     *
     * async function example2() {
     *   const resp = await Request.get('/path/to/url')
     *     .asAny()
     *     .getResponse()
     *   return resp.data
     * }
     * ```
     */
    Response.prototype.getData = function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.getResponse()];
                    case 1: return [2 /*return*/, (_a.sent()).data];
                }
            });
        });
    };
    /**
     * Makes a HTTP request and returns the response content.
     *
     * ```typescript
     * import {Request} from 'simpli-web-sdk'
     *
     * async function example() {
     *   return await Request.get('/path/to/url')
     *     .asAny()
     *     .getResponse()
     * }
     * ```
     */
    Response.prototype.getResponse = function (classTransformOptions) {
        return __awaiter(this, void 0, void 0, function () {
            var _a, axiosConfig, responseType, requestName, requestDelay, endpoint, event, response, e_1;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _a = this, axiosConfig = _a.axiosConfig, responseType = _a.responseType, requestName = _a.requestName, requestDelay = _a.requestDelay, endpoint = _a.endpoint;
                        event = responseType;
                        if (event && typeof event.onBeforeResponse === 'function') {
                            event.onBeforeResponse(this);
                        }
                        RequestListener.emitRequestStart(requestName || endpoint);
                        response = null;
                        _b.label = 1;
                    case 1:
                        _b.trys.push([1, 5, , 6]);
                        if (!requestDelay) return [3 /*break*/, 3];
                        return [4 /*yield*/, this.sleep(requestDelay)];
                    case 2:
                        _b.sent();
                        _b.label = 3;
                    case 3: return [4 /*yield*/, RequestConfig.axios.request(axiosConfig)];
                    case 4:
                        response = _b.sent();
                        return [3 /*break*/, 6];
                    case 5:
                        e_1 = _b.sent();
                        RequestListener.emitRequestError(requestName || endpoint);
                        throw e_1;
                    case 6:
                        RequestListener.emitRequestEnd(requestName || endpoint);
                        if (event && typeof event.onBeforeSerialization === 'function') {
                            event.onBeforeSerialization(response, this);
                        }
                        if (response.data === undefined) {
                            response.data = JSON.parse(response.request.response || '{}');
                        }
                        if (responseType === undefined) {
                            return [2 /*return*/, response];
                        }
                        if (typeof responseType === 'object') {
                            // Class object instance from constructor (new CustomClass())
                            // The instance will be automatically populated
                            response.data = plainToClassFromExist(responseType, response.data, classTransformOptions);
                        }
                        else if (typeof responseType === 'function') {
                            // Class constructor (CustomClass, Number, String, Boolean, etc.)
                            response.data = plainToClass(responseType, response.data, classTransformOptions);
                        }
                        else
                            throw Error('Error: Entity should be either a Class or ClassObject');
                        if (event && typeof event.onAfterSerialization === 'function') {
                            event.onAfterSerialization(response, this);
                        }
                        return [2 /*return*/, response];
                }
            });
        });
    };
    Response.prototype.sleep = function (ms) {
        return new Promise(function (resolve) { return setTimeout(resolve, ms); });
    };
    return Response;
}());

var Request = /** @class */ (function () {
    function Request(axiosConfig) {
        this.axiosConfig = axiosConfig;
    }
    Request.get = function (url, axiosConfig) {
        var localConfig = { method: 'GET', url: url };
        return new Request(Object.assign(localConfig, axiosConfig));
    };
    Request.delete = function (url, axiosConfig) {
        var localConfig = { method: 'DELETE', url: url };
        return new Request(Object.assign(localConfig, axiosConfig));
    };
    Request.head = function (url, axiosConfig) {
        var localConfig = { method: 'HEAD', url: url };
        return new Request(Object.assign(localConfig, axiosConfig));
    };
    Request.post = function (url, data, axiosConfig) {
        var localConfig = { method: 'POST', url: url, data: classToPlain(data) };
        return new Request(Object.assign(localConfig, axiosConfig));
    };
    Request.put = function (url, data, axiosConfig) {
        var localConfig = { method: 'PUT', url: url, data: classToPlain(data) };
        return new Request(Object.assign(localConfig, axiosConfig));
    };
    Request.patch = function (url, data, axiosConfig) {
        var localConfig = { method: 'PATCH', url: url, data: classToPlain(data) };
        return new Request(Object.assign(localConfig, axiosConfig));
    };
    Object.defineProperty(Request.prototype, "endpoint", {
        get: function () {
            var url = this.axiosConfig.url || '';
            return url.replace(/^(?:https?:)?\/\/.*(?=\.)[^\/]*/g, '').replace(/(?=\?).*/g, '');
        },
        enumerable: true,
        configurable: true
    });
    Request.prototype.name = function (requestName) {
        this.requestName = requestName;
        return this;
    };
    Request.prototype.delay = function (requestDelay) {
        this.requestDelay = Math.max(requestDelay, 0);
        return this;
    };
    Request.prototype.as = function (responseType) {
        return new Response(this, responseType);
    };
    Request.prototype.asArrayOf = function (responseType) {
        return new Response(this, responseType);
    };
    Request.prototype.asAny = function () {
        return new Response(this);
    };
    Request.prototype.asVoid = function () {
        return new Response(this);
    };
    Request.prototype.asString = function () {
        return new Response(this);
    };
    Request.prototype.asNumber = function () {
        return new Response(this);
    };
    Request.prototype.asBoolean = function () {
        return new Response(this);
    };
    return Request;
}());

/* istanbul ignore file */
var SocketConnection = /** @class */ (function () {
    function SocketConnection(url) {
        this.socket = new WebSocket(encodeURI(url));
    }
    SocketConnection.prototype.as = function (classType) {
        this.classType = classType;
        return this;
    };
    SocketConnection.prototype.disconnect = function () {
        this.socket.close();
    };
    SocketConnection.prototype.onOpen = function (callback) {
        this.socket.onopen = function (e) {
            callback(e);
        };
    };
    SocketConnection.prototype.onClose = function (callback) {
        this.socket.onclose = function (e) {
            callback(e);
        };
    };
    SocketConnection.prototype.onError = function (callback) {
        this.socket.onerror = function (e) {
            callback(e);
        };
    };
    SocketConnection.prototype.onData = function (callback) {
        var self = this;
        this.socket.onmessage = function (e) {
            if (!self.classType) {
                callback(e.data);
                return;
            }
            var data = e.data;
            var resp = deserialize(self.classType, data);
            callback(resp);
        };
    };
    SocketConnection.prototype.sendText = function (text) {
        this.socket.send(text);
    };
    SocketConnection.prototype.sendObject = function (obj) {
        this.sendText(JSON.stringify(classToPlain(obj)));
    };
    SocketConnection.prototype.onOpenPromise = function () {
        var _this = this;
        var promiseFunc = function (resolve) {
            _this.onOpen(function (e) { return resolve(e); });
        };
        return new Promise(promiseFunc);
    };
    SocketConnection.prototype.onClosePromise = function () {
        var _this = this;
        var promiseFunc = function (resolve) {
            _this.onClose(function (e) { return resolve(e); });
        };
        return new Promise(promiseFunc);
    };
    SocketConnection.prototype.onErrorPromise = function () {
        var _this = this;
        var promiseFunc = function (resolve) {
            _this.onError(function (e) { return resolve(e); });
        };
        return new Promise(promiseFunc);
    };
    SocketConnection.prototype.onDataPromise = function () {
        var _this = this;
        var promiseFunc = function (resolve) {
            _this.onData(function (resp) { return resolve(resp); });
        };
        return new Promise(promiseFunc);
    };
    return SocketConnection;
}());

/* istanbul ignore file */
function ResponseSerialize(func) {
    return Type(function () { return func; });
}
function ResponseExpose(name) {
    return Expose({ name: name, toClassOnly: true });
}
function RequestExpose(name) {
    return Expose({ name: name, toPlainOnly: true });
}
function HttpExpose(name) {
    return Expose({ name: name });
}
function RequestExclude() {
    return Exclude({ toPlainOnly: true });
}
function ResponseExclude() {
    return Exclude({ toClassOnly: true });
}
function HttpExclude() {
    return Exclude();
}

export { RequestListener, Request, Response, SocketConnection, RequestConfig, ResponseSerialize, ResponseExpose, RequestExpose, HttpExpose, RequestExclude, ResponseExclude, HttpExclude };
//# sourceMappingURL=serialized-request.es5.js.map
